<template>
    <ul class="accordion list list-unstyled tw-m-0 tw-border-solid tw-border tw-border-gray-100">
        <li v-for="type in goalTypes" :key="type" class="accordion__item">
            <div
                class="accordion__nav tw-bg-gray-50 tw-transition-colors hover:tw-bg-gray-50 focus:tw-bg-gray-50 tw-px-4 tw-py-2 tw-cursor-pointer tw-text-gray-600 tw-font-bold tw-border-0 tw-border-solid tw-border-b tw-border-gray-100"
                :class="{ active: selectedSection === type }"
                tabindex="0"
                @click="selectedSection = type"
                @keyup.enter="selectedSection = type"
            >
                <i
                    class="tw-align-baseline tw-mr-2"
                    :class="[selectedSection === type ? 'fa fa-caret-down' : 'fa fa-caret-right']"
                    aria-hidden="true"
                />{{ $t(`goals.update.parentGoalSelection.${type}Title`) }}
            </div>

            <div
                v-if="type === 'search'"
                class="accordion__body tw-p-4"
                :class="[
                    selectedSection === type
                        ? 'tw-block tw-border-0 tw-border-solid tw-border-l-4 tw-border-gray-100'
                        : 'tw-hidden',
                ]"
            >
                <l-input-typeahead
                    label=""
                    :placeholder="$t(`goals.update.placeholderParent`)"
                    :show-picture="false"
                    :data="typeaheadData"
                    :on-select="onGoalSelect"
                    :on-input-change="onGoalSearchInput"
                    :show-all-results="true"
                    position="bottom"
                />

                <div v-show="!typeaheadData.length">
                    <i class="fal fa-search tw-mr-1"></i>
                    {{ $t(`goals.update.helpParentNoneFound`) }}
                    <a
                        href="https://leapsome.zendesk.com/hc/en-us/articles/360011384473"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {{ $t(`goals.update.helpParentNoneFoundLink`) }}</a
                    >
                </div>
            </div>

            <ul
                v-else
                class="accordion__body list list-unstyled tw-p-4"
                :class="[
                    selectedSection === type
                        ? 'tw-block tw-border-0 tw-border-solid tw-border-l-4 tw-border-gray-100'
                        : 'tw-hidden',
                ]"
            >
                <li v-if="!groupedGoalList[type].length">
                    {{ $t(`goals.update.parentGoalSelection.notFound`) }}
                </li>
                <li v-for="goal in groupedGoalList[type]" :key="`${type[0]}__${goal._id}`">
                    <a
                        href="#"
                        tabindex="0"
                        class="tw-flex tw-space-x-4 tw-items-center tw-py-2 tw-border-0 tw-border-solid tw-border-b tw-border-gray-100"
                        @click.prevent="onGoalSelect(goal)"
                        @keyup.enter="onGoalSelect(goal)"
                    >
                        <img
                            :src="goal.icon"
                            class="tw-flex-grow-0 tw-rounded-full tw-w-8 tw-h-8"
                            alt="Goal Icon"
                        />
                        <div class="tw-flex-1">
                            {{ goal.name }}<br />
                            <div class="tw-text-gray-400 tw-text-xs">
                                <i :class="goal.contributionIcon" class="tw-mr-1"></i>
                                {{ goal.contributionTag }}
                            </div>
                        </div>
                    </a>
                </li>
            </ul>
        </li>
    </ul>
</template>

<script>
import { mapGetters } from "vuex";

import LInputTypeahead from "@/components/LInputTypeahead";

export default {
    components: {
        LInputTypeahead,
    },
    props: {
        excludedGoalId: {
            type: String,
            default: "",
        },
        selectedParentGoal: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            selectedSection: "search",
            goalTypes: Object.freeze(["search", "company", "team", "personal"]),
        };
    },
    created() {
        this.$store.dispatch({
            type: "goals/goals/getList",
            payload: {
                listSegment: "custom",
                listCustomFilter: {
                    states: ["live"],
                },
                output: "list",
            },
        });
    },
    computed: {
        ...mapGetters({
            goalList: "goals/goals/list",
            goalSearchList: "goals/search/list",
        }),
        groupedGoalList() {
            if (!Array.isArray(this.goalList)) {
                return { company: [], team: [], personal: [] };
            }

            // We filter the current goal from the list of grouped goals
            // so that we don't allow the user to align a goal with itself
            const list = this.goalList.filter(goal => goal._id !== this.excludedGoalId);

            const company = list.filter(goal => goal.type === "goalTeam");

            const team = list
                .filter(goal => goal.type === "goalLocalTeam")
                .sort((a, b) => a.name?.localeCompare(b.name));

            const personal = list
                .filter(goal => goal.type === "goalUser")
                .sort((a, b) => a.name?.localeCompare(b.name));

            return { company, team, personal };
        },
        typeaheadData() {
            return this.goalSearchList.map(goal => {
                let desc;

                switch (goal.type) {
                    case "goalTeam":
                        desc = "Company";
                        break;
                    case "goalLocalTeam":
                        desc = goal.localTeam?.name;
                        break;
                    case "goalUser":
                        desc = goal.user?.fullname;
                        break;
                    default:
                        desc = "";
                }

                return {
                    ...goal,
                    desc,
                };
            });
        },
    },
    methods: {
        onGoalSelect(goal) {
            this.$emit("update:selectedParentGoal", goal || null);
        },
        onGoalSearchInput(query = "") {
            this.$store.dispatch({
                type: "goals/search/findByName",
                payload: {
                    query: query.length >= 2 ? query : undefined,
                    exclude: this.excludedGoalId,
                },
            });
        },
    },
};
</script>

<style scoped>
.accordion__item:last-child .accordion__nav {
    border-bottom: none;
}

.accordion__nav.active {
    border-left: 3px solid var(--color-primary);
}

ul.accordion__body > li:last-child > a {
    border-bottom: none;
    padding-bottom: 0;
}

ul.accordion__body > li:first-child > a {
    padding-top: 0;
}
</style>
